import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Switch, FormControlLabel, TextField,
  Button, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, MenuItem, Chip, CircularProgress, Alert,
  Snackbar, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Autocomplete, Checkbox,
  Radio, RadioGroup, FormControl, FormLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getCollection } from '../../services/firestoreService';
import { useBranch } from '../../context/BranchContext';
import { todayLocalStr } from '../../utils/dateUtils';
import { db } from '../../firebase';
import {
  collection, doc, setDoc, updateDoc, getDoc, getDocs, addDoc, deleteDoc, query, where,
} from 'firebase/firestore';

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const WORKING_DAYS = [0, 1, 2, 3, 4, 5, 6]; // Dom–Sáb

const DEFAULT_SLOTS = [
  { time: '09:00', label: '09:00', capacity: 1 },
  { time: '09:30', label: '09:30', capacity: 1 },
  { time: '10:00', label: '10:00', capacity: 1 },
  { time: '10:30', label: '10:30', capacity: 1 },
  { time: '11:00', label: '11:00', capacity: 1 },
  { time: '11:30', label: '11:30', capacity: 1 },
  { time: '14:00', label: '14:00', capacity: 1 },
  { time: '14:30', label: '14:30', capacity: 1 },
  { time: '15:00', label: '15:00', capacity: 1 },
  { time: '15:30', label: '15:30', capacity: 1 },
  { time: '16:00', label: '16:00', capacity: 1 },
  { time: '16:30', label: '16:30', capacity: 1 },
];

const EMPTY_BLOCK_FORM = {
  date: '', reason: '', blockType: 'full_day', startTime: '', endTime: '',
};

const ProfessionalScheduleManager = () => {
  const { isAdmin, activeBranchId, accessibleBranches, filterByBranch } = useBranch();
  const [clinics, setClinics] = useState([]);
  const [branches, setBranches] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [selectedProfId, setSelectedProfId] = useState('');
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState('');
  const [selectedCapacityId, setSelectedCapacityId] = useState('');
  const [schedule, setSchedule] = useState({});
  const [scheduleDates, setScheduleDates] = useState({ startDate: '', endDate: '' });
  const [recurrence, setRecurrence] = useState({
    type: 'continuous', // 'continuous' | 'biweekly' | 'monthly'
    weekOrdinal: '', // '' | 'first' | 'second' | 'third' | 'last' (solo monthly)
  });
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSlot, setNewSlot] = useState({ time: '', capacity: 1 });
  const [blockDialog, setBlockDialog] = useState(false);
  const [blockForm, setBlockForm] = useState(EMPTY_BLOCK_FORM);
  const [savingBlock, setSavingBlock] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [selectedCapacityData, setSelectedCapacityData] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, schedule: null });
  const [occupiedSlots, setOccupiedSlots] = useState({});
  const [selectedScheduleId, setSelectedScheduleId] = useState('');

  useEffect(() => {
    Promise.all([
      getCollection('clinics'),
      getCollection('professionals'),
      getCollection('specialties'),
    ]).then(([clinicsData, profsData, specsData]) => {
      setClinics(filterByBranch(clinicsData).filter((c) => c.active !== false).sort((a, b) => a.name.localeCompare(b.name)));
      setBranches(accessibleBranches);
      setProfessionals(filterByBranch(profsData).filter((p) => p.active !== false).sort((a, b) => (a.apellidos || '').localeCompare(b.apellidos || '')));
      if (!isAdmin) {
        setSelectedBranchId(activeBranchId);
      }
    });
  }, [accessibleBranches, isAdmin, activeBranchId]);

  const [allSpecialties, setAllSpecialties] = useState([]);
  const [capacities, setCapacities] = useState([]);
  useEffect(() => {
    Promise.all([
      getCollection('specialties'),
      getCollection('capacities'),
    ]).then(([specs, caps]) => {
      setAllSpecialties(specs.filter((s) => s.active !== false));
      setCapacities(filterByBranch(caps).filter((c) => c.type === 'consulta').sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      }));
    });
  }, []);

  // Load existing schedules
  useEffect(() => {
    setLoadingSchedules(true);
    getCollection('professionalSchedules').then((schedules) => {
      const visibleSchedules = isAdmin ? schedules : filterByBranch(schedules);
      const enriched = visibleSchedules.map((s) => {
        const parts = s.id.split('_');
        const pid = s.professionalId || parts[0] || '';
        const cid = s.clinicId || parts[1] || '';
        const sid = s.specialtyId || parts[2] || '';
        const prof = professionals.find((p) => p.id === pid);
        const clinic = clinics.find((c) => c.id === cid);
        const spec = allSpecialties.find((sp) => sp.id === sid);
        const cap = capacities.find((c) => c.id === s.capacityId);
        const branch = branches.find((b) => b.id === s.branchId);
        return {
          ...s,
          professionalName: prof ? `${prof.nombre} ${prof.apellidos}` : 'Desconocido',
          clinicName: clinic?.name || 'Desconocida',
          specialtyName: spec?.name || 'Desconocida',
          capacityNumber: cap?.number || 'Desconocida',
          branchName: branch?.name || 'Sin sucursal',
        };
      });
      setExistingSchedules(enriched);
    }).finally(() => setLoadingSchedules(false));
  }, [professionals, clinics, allSpecialties, capacities, branches]);

  const loadScheduleData = useCallback(async () => {
    if (!selectedProfId || !selectedClinicId || !selectedSpecialtyId) return;
    setLoading(true);
    try {
      // Load schedule for the combination: professionalId + clinicId + specialtyId
      const scheduleKey = `${selectedProfId}_${selectedClinicId}_${selectedSpecialtyId}`;
      const schedSnap = await getDoc(doc(db, 'professionalSchedules', scheduleKey));
      if (schedSnap.exists()) {
        const data = schedSnap.data();
        setSchedule(data.schedule || {});
        setScheduleDates({
          startDate: data.startDate || '',
          endDate: data.endDate || '',
        });
        setRecurrence({
          type: data.recurrenceType || 'continuous',
          weekOrdinal: data.recurrenceWeekOrdinal || '',
        });
        const capacityId = data.capacityId || '';
        setSelectedCapacityId(capacityId);
        // Load capacity data
        const capacity = capacities.find((c) => c.id === capacityId);
        setSelectedCapacityData(capacity || null);

        // Load occupied slots for this capacity
        if (capacityId) {
          const allSchedules = await getCollection('professionalSchedules');
          const capacitySchedules = allSchedules.filter(s => s.capacityId === capacityId && s.id !== scheduleKey);

          const occupied = {};
          capacitySchedules.forEach(sched => {
            if (sched.schedule) {
              Object.keys(sched.schedule).forEach(day => {
                const dayConfig = sched.schedule[day];
                if (dayConfig?.active && dayConfig.slots) {
                  if (!occupied[day]) occupied[day] = [];
                  dayConfig.slots.forEach(slot => {
                    if (!occupied[day].includes(slot.time)) {
                      occupied[day].push(slot.time);
                    }
                  });
                }
              });
            }
          });
          setOccupiedSlots(occupied);
        }
      } else {
        const defaultSched = {};
        WORKING_DAYS.forEach((d) => {
          defaultSched[String(d)] = { active: false, slots: [...DEFAULT_SLOTS] };
        });
        setSchedule(defaultSched);
        setScheduleDates({ startDate: '', endDate: '' });
        setRecurrence({ type: 'continuous', weekOrdinal: '' });
        setSelectedCapacityId('');
        setSelectedCapacityData(null);
        setOccupiedSlots({});
      }

      // Load blocks for this combination (filter client-side to avoid composite index)
      const today = todayLocalStr();
      const blocksSnap = await getDocs(
        query(
          collection(db, 'scheduleBlocks'),
          where('professionalId', '==', selectedProfId)
        )
      );
      setBlocks(
        blocksSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) =>
            b.clinicId === selectedClinicId &&
            b.specialtyId === selectedSpecialtyId &&
            b.date >= today
          )
          .sort((a, b) => a.date.localeCompare(b.date))
      );
    } finally {
      setLoading(false);
    }
  }, [selectedProfId, selectedClinicId, selectedSpecialtyId, capacities]);

  const handleClinicChange = (clinicId) => {
    setSelectedClinicId(clinicId);
    setSelectedProfId('');
    setSelectedSpecialtyId('');
    setSelectedCapacityId('');
    setSelectedScheduleId('');
    setSchedule({});
    setScheduleDates({ startDate: '', endDate: '' });
    setRecurrence({ type: 'continuous', weekOrdinal: '' });
    setBlocks([]);
  };

  const handleBranchChange = (branchId) => {
    setSelectedBranchId(branchId);
    setSelectedClinicId('');
    setSelectedProfId('');
    setSelectedSpecialtyId('');
    setSelectedCapacityId('');
    setSelectedScheduleId('');
    setSchedule({});
    setScheduleDates({ startDate: '', endDate: '' });
    setRecurrence({ type: 'continuous', weekOrdinal: '' });
    setBlocks([]);
  };

  // Filter capacities by selected branch
  const filteredCapacities = useMemo(() => {
    if (!selectedBranchId) return capacities;
    return capacities.filter((c) => c.branchIds?.includes(selectedBranchId));
  }, [capacities, selectedBranchId]);

  const handleProfChange = (profId) => {
    setSelectedProfId(profId);
    setSelectedSpecialtyId('');
    setSelectedCapacityId('');
    setSelectedScheduleId('');
    setSchedule({});
    setScheduleDates({ startDate: '', endDate: '' });
    setRecurrence({ type: 'continuous', weekOrdinal: '' });
    setBlocks([]);
  };

  const handleEditSchedule = async (sched) => {
    setSelectedScheduleId(sched.id);
    setSelectedBranchId(sched.branchId || '');
    setSelectedClinicId(sched.clinicId || '');
    setSelectedProfId(sched.professionalId || '');
    setSelectedSpecialtyId(sched.specialtyId || '');
    setSelectedCapacityId(sched.capacityId || '');
    setSchedule(sched.schedule || {});
    setScheduleDates({
      startDate: sched.startDate || '',
      endDate: sched.endDate || '',
    });
    setRecurrence({
      type: sched.recurrenceType || 'continuous',
      weekOrdinal: sched.recurrenceWeekOrdinal || '',
    });
    // Load capacity data
    const capacity = capacities.find((c) => c.id === sched.capacityId);
    setSelectedCapacityData(capacity || null);
    // Load blocks for this schedule
    setLoading(true);
    try {
      const today = todayLocalStr();
      const blocksSnap = await getDocs(
        query(
          collection(db, 'scheduleBlocks'),
          where('professionalId', '==', sched.professionalId)
        )
      );
      setBlocks(
        blocksSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) =>
            (b.scheduleId === sched.id ||
              (!b.scheduleId && b.clinicId === sched.clinicId && b.specialtyId === sched.specialtyId)) &&
            b.date >= today
          )
          .sort((a, b) => a.date.localeCompare(b.date))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (sched) => {
    if (window.confirm('¿Está seguro de eliminar esta agenda? Esta acción no se puede deshacer.')) {
      try {
        await deleteDoc(doc(db, 'professionalSchedules', sched.id));
        setSnackbar({ open: true, message: 'Agenda eliminada exitosamente', severity: 'success' });
        // Reload existing schedules
        const rawSchedules = await getCollection('professionalSchedules');
        const visibleSchedules = isAdmin ? rawSchedules : filterByBranch(rawSchedules);
        const enriched = visibleSchedules.map((s) => {
          const parts = s.id.split('_');
          const pid = s.professionalId || parts[0] || '';
          const cid = s.clinicId || parts[1] || '';
          const sid = s.specialtyId || parts[2] || '';
          const prof = professionals.find((p) => p.id === pid);
          const clinic = clinics.find((c) => c.id === cid);
          const spec = allSpecialties.find((sp) => sp.id === sid);
          const cap = capacities.find((c) => c.id === s.capacityId);
          const branch = branches.find((b) => b.id === s.branchId);
          return {
            ...s,
            professionalName: prof ? `${prof.nombre} ${prof.apellidos}` : 'Desconocido',
            clinicName: clinic?.name || 'Desconocida',
            specialtyName: spec?.name || 'Desconocida',
            capacityNumber: cap?.number || 'Desconocida',
            branchName: branch?.name || 'Sin sucursal',
          };
        });
        setExistingSchedules(enriched);
      } catch (error) {
        console.error('Error deleting schedule:', error);
        setSnackbar({ open: true, message: 'Error al eliminar agenda', severity: 'error' });
      }
    }
  };

  const handlePreviewSchedule = (sched) => {
    const parts = sched.id.split('_');
    const pid = sched.professionalId || parts[0] || '';
    const cid = sched.clinicId || parts[1] || '';
    const sid = sched.specialtyId || parts[2] || '';
    const prof = professionals.find((p) => p.id === pid);
    const clinic = clinics.find((c) => c.id === cid);
    const spec = allSpecialties.find((s) => s.id === sid);
    const cap = capacities.find((c) => c.id === sched.capacityId);
    const branch = branches.find((b) => b.id === sched.branchId);
    setPreviewDialog({
      open: true,
      schedule: {
        ...sched,
        professionalName: prof ? `${prof.nombre} ${prof.apellidos}` : 'Desconocido',
        clinicName: clinic?.name || 'Desconocida',
        specialtyName: spec?.name || 'Desconocida',
        capacityNumber: cap?.number || 'Desconocida',
        capacityLocation: cap?.location || '',
        branchName: branch?.name || 'Sin sucursal',
      }
    });
  };

  const handleSpecialtyChange = (specialtyId) => {
    setSelectedSpecialtyId(specialtyId);
    setSelectedCapacityId('');
    setSelectedScheduleId('');
    setSchedule({});
    setScheduleDates({ startDate: '', endDate: '' });
    setRecurrence({ type: 'continuous', weekOrdinal: '' });
    setBlocks([]);
  };

  const handleCapacityChange = async (capacityId) => {
    setSelectedCapacityId(capacityId);
    const capacity = capacities.find((c) => c.id === capacityId);
    setSelectedCapacityData(capacity || null);

    // Cargar horarios ocupados para esta capacidad
    if (capacityId) {
      try {
        const rawSchedules = await getCollection('professionalSchedules');
        const allSchedules = isAdmin ? rawSchedules : filterByBranch(rawSchedules);
        const capacitySchedules = allSchedules.filter(s => s.capacityId === capacityId && s.id !== selectedScheduleId);

        const occupied = {};
        capacitySchedules.forEach(sched => {
          if (sched.schedule) {
            Object.keys(sched.schedule).forEach(day => {
              const dayConfig = sched.schedule[day];
              if (dayConfig?.active && dayConfig.slots) {
                if (!occupied[day]) occupied[day] = [];
                dayConfig.slots.forEach(slot => {
                  if (!occupied[day].includes(slot.time)) {
                    occupied[day].push(slot.time);
                  }
                });
              }
            });
          }
        });
        setOccupiedSlots(occupied);
      } catch (error) {
        console.error('Error loading occupied slots:', error);
        setOccupiedSlots({});
      }
    } else {
      setOccupiedSlots({});
    }
  };

  const toggleDay = (day) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { ...prev[day], active: !prev[day]?.active },
    }));
  };

  const updateCapacity = (day, time, val) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: (prev[day]?.slots || []).map((s) =>
          s.time === time ? { ...s, capacity: parseInt(val) || 1 } : s
        ),
      },
    }));
  };

  const toggleVisibleOnline = (day, time) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: (prev[day]?.slots || []).map((s) =>
          s.time === time ? { ...s, visibleOnline: !s.visibleOnline } : s
        ),
      },
    }));
  };

  const removeSlot = (day, time) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: (prev[day]?.slots || []).filter((s) => s.time !== time),
      },
    }));
  };

  const addSlot = (day) => {
    if (!newSlot.time) return;
    const slots = schedule[day]?.slots || [];
    if (slots.find((s) => s.time === newSlot.time)) {
      setSnackbar({ open: true, message: 'Ese horario ya existe', severity: 'warning' });
      return;
    }
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        slots: [...slots, { time: newSlot.time, label: newSlot.time, capacity: parseInt(newSlot.capacity) || 1, visibleOnline: true }]
          .sort((a, b) => a.time.localeCompare(b.time)),
      },
    }));
    setNewSlot({ time: '', capacity: 1 });
  };

  const copyToAllDays = (sourceDay) => {
    const sourceSlots = schedule[sourceDay]?.slots || [];
    setSchedule((prev) => {
      const updated = { ...prev };
      WORKING_DAYS.forEach((d) => {
        if (String(d) !== String(sourceDay)) {
          updated[String(d)] = { ...updated[String(d)], slots: sourceSlots.map(s => ({ ...s })) };
        }
      });
      return updated;
    });
    setSnackbar({ open: true, message: 'Horarios copiados a todos los días', severity: 'info' });
  };

  const validateScheduleWithinCapacityHours = () => {
    if (!selectedCapacityData || !selectedCapacityData.operatingHours) {
      setSnackbar({ open: true, message: 'La capacidad no tiene horario de funcionamiento configurado', severity: 'warning' });
      return false;
    }

    const { days: capacityDays, startTime: capacityStart, endTime: capacityEnd } = selectedCapacityData.operatingHours;

    // Validar días
    const activeDays = Object.keys(schedule).filter(day => schedule[day]?.active);
    const invalidDays = activeDays.filter(day => !capacityDays.includes(parseInt(day)));

    if (invalidDays.length > 0) {
      const dayNames = invalidDays.map(d => DAY_NAMES[parseInt(d)]).join(', ');
      setSnackbar({ open: true, message: `Los días ${dayNames} no están en el horario de funcionamiento de la capacidad`, severity: 'warning' });
      return false;
    }

    // Validar horarios de slots
    const [capStartHour, capStartMin] = capacityStart.split(':').map(Number);
    const [capEndHour, capEndMin] = capacityEnd.split(':').map(Number);
    const capStartTotal = capStartHour * 60 + capStartMin;
    const capEndTotal = capEndHour * 60 + capEndMin;

    for (const day of activeDays) {
      const slots = schedule[day]?.slots || [];
      for (const slot of slots) {
        const [slotHour, slotMin] = slot.time.split(':').map(Number);
        const slotTotal = slotHour * 60 + slotMin;

        if (slotTotal < capStartTotal || slotTotal >= capEndTotal) {
          setSnackbar({
            open: true,
            message: `El slot ${slot.time} del día ${DAY_NAMES[parseInt(day)]} está fuera del horario de funcionamiento (${capacityStart}-${capacityEnd})`,
            severity: 'warning'
          });
          return false;
        }
      }
    }

    return true;
  };

  const calculateScheduleHours = (scheduleData) => {
    let totalHours = 0;
    const activeDays = Object.keys(scheduleData).filter(day => scheduleData[day]?.active);

    activeDays.forEach(day => {
      const slots = scheduleData[day]?.slots || [];
      // A8: Calcular horas reales basadas en el intervalo entre slots (no 1 slot = 1 hora)
      if (slots.length === 0) return;
      if (slots.length === 1) {
        totalHours += 0.5; // Un slot aislado = 30 min por defecto
        return;
      }
      const sortedSlots = [...slots].sort((a, b) => a.time.localeCompare(b.time));
      const first = sortedSlots[0].time;
      const last = sortedSlots[sortedSlots.length - 1].time;
      const [fh, fm] = first.split(':').map(Number);
      const [lh, lm] = last.split(':').map(Number);
      const totalMin = (lh * 60 + lm) - (fh * 60 + fm);
      // Sumar el intervalo del último slot (30 min por defecto)
      totalHours += (totalMin + 30) / 60;
    });

    return totalHours;
  };

  const validateCapacityAvailability = async () => {
    if (!selectedCapacityId) return true;

    try {
      // Cargar todas las agendas para esta capacidad
      const rawSchedules = await getCollection('professionalSchedules');
      const allSchedules = isAdmin ? rawSchedules : filterByBranch(rawSchedules);
      const capacitySchedules = allSchedules.filter(s => s.capacityId === selectedCapacityId && s.id !== selectedScheduleId);

      // Validar solapamiento de horarios específicos
      const activeDays = Object.keys(schedule).filter(day => schedule[day]?.active);

      for (const day of activeDays) {
        const newSlots = schedule[day]?.slots || [];

        for (const newSlot of newSlots) {
          // Verificar si este slot ya está ocupado por otra agenda
          for (const existingSched of capacitySchedules) {
            const existingDayConfig = existingSched.schedule?.[day];
            if (existingDayConfig?.active) {
              const existingSlots = existingDayConfig.slots || [];
              const isSlotOccupied = existingSlots.some(s => s.time === newSlot.time);

              if (isSlotOccupied) {
                setSnackbar({
                  open: true,
                  message: `El horario ${newSlot.time} del día ${DAY_NAMES[parseInt(day)]} ya está ocupado por otra agenda en esta consulta`,
                  severity: 'warning'
                });
                return false;
              }
            }
          }
        }
      }

      // Calcular horas ocupadas por agendas existentes (excluyendo la actual)
      let occupiedHours = 0;
      capacitySchedules.forEach(sched => {
        if (sched.schedule) {
          occupiedHours += calculateScheduleHours(sched.schedule);
        }
      });

      // Calcular horas de la nueva agenda
      const newScheduleHours = calculateScheduleHours(schedule);

      // Calcular capacidad semanal de la capacidad
      const capacityWeeklyHours = selectedCapacityData?.weeklyCapacity || 0;

      if (occupiedHours + newScheduleHours > capacityWeeklyHours) {
        setSnackbar({
          open: true,
          message: `La capacidad no tiene horas suficientes. Ocupadas: ${occupiedHours.toFixed(1)}h, Nueva agenda: ${newScheduleHours.toFixed(1)}h, Disponible: ${capacityWeeklyHours}h`,
          severity: 'warning'
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating capacity availability:', error);
      setSnackbar({ open: true, message: 'Error al validar disponibilidad de capacidad', severity: 'error' });
      return false;
    }
  };

  const handleSaveSchedule = async () => {
    if (!selectedProfId || !selectedClinicId || !selectedSpecialtyId || !selectedCapacityId) return;
    if (!scheduleDates.startDate) {
      setSnackbar({ open: true, message: 'La fecha de inicio es obligatoria', severity: 'warning' });
      return;
    }

    // Validar que la agenda esté dentro del horario de funcionamiento de la capacidad
    if (!validateScheduleWithinCapacityHours()) {
      return;
    }

    // Validar disponibilidad de capacidad
    const isAvailable = await validateCapacityAvailability();
    if (!isAvailable) {
      return;
    }

    setSaving(true);
    try {
      const scheduleHours = calculateScheduleHours(schedule);
      const scheduleData = {
        professionalId: selectedProfId,
        clinicId: selectedClinicId,
        specialtyId: selectedSpecialtyId,
        capacityId: selectedCapacityId,
        branchId: isAdmin ? (selectedBranchId || null) : (selectedBranchId || activeBranchId),
        startDate: scheduleDates.startDate,
        endDate: scheduleDates.endDate || null,
        recurrenceType: recurrence.type,
        recurrenceWeekOrdinal: recurrence.type === 'monthly' ? recurrence.weekOrdinal : '',
        schedule,
        scheduleHours: scheduleHours,
        updatedAt: new Date(),
      };

      let scheduleRef;
      if (selectedScheduleId) {
        scheduleRef = doc(db, 'professionalSchedules', selectedScheduleId);
        await updateDoc(scheduleRef, scheduleData);
      } else {
        scheduleRef = doc(collection(db, 'professionalSchedules'));
        await setDoc(scheduleRef, { ...scheduleData, scheduleId: scheduleRef.id, createdAt: new Date() });
        setSelectedScheduleId(scheduleRef.id);
      }

      setSnackbar({ open: true, message: 'Agenda guardada exitosamente', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlock = async () => {
    if (!selectedScheduleId) {
      setSnackbar({ open: true, message: 'Guarda la agenda antes de agregar bloqueos', severity: 'warning' });
      return;
    }
    if (!blockForm.date || !blockForm.reason.trim()) {
      setSnackbar({ open: true, message: 'Fecha y motivo son obligatorios', severity: 'warning' });
      return;
    }
    if (blockForm.blockType === 'time_range' && (!blockForm.startTime || !blockForm.endTime)) {
      setSnackbar({ open: true, message: 'Ingresa hora de inicio y fin', severity: 'warning' });
      return;
    }
    setSavingBlock(true);
    try {
      const blockData = {
        scheduleId: selectedScheduleId,
        professionalId: selectedProfId,
        clinicId: selectedClinicId,
        specialtyId: selectedSpecialtyId,
        branchId: isAdmin ? (selectedBranchId || null) : activeBranchId,
        date: blockForm.date,
        reason: blockForm.reason.trim(),
        blockType: blockForm.blockType,
        startTime: blockForm.blockType === 'time_range' ? blockForm.startTime : null,
        endTime: blockForm.blockType === 'time_range' ? blockForm.endTime : null,
        createdAt: new Date(),
      };
      await addDoc(collection(db, 'scheduleBlocks'), blockData);
      setSnackbar({ open: true, message: 'Bloqueo registrado', severity: 'success' });
      setBlockDialog(false);
      setBlockForm(EMPTY_BLOCK_FORM);
      loadScheduleData();
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al registrar bloqueo', severity: 'error' });
    } finally {
      setSavingBlock(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('¿Eliminar este bloqueo?')) return;
    await deleteDoc(doc(db, 'scheduleBlocks', blockId));
    setSnackbar({ open: true, message: 'Bloqueo eliminado', severity: 'info' });
    loadScheduleData();
  };

  const selectedProf = professionals.find((p) => p.id === selectedProfId);
  const filteredProfessionals = selectedClinicId
    ? professionals.filter((p) => p.clinicIds?.includes(selectedClinicId) || p.clinicId === selectedClinicId)
    : professionals;

  // Get specialties for the selected professional
  const profSpecialtyIds = selectedProf
    ? (selectedProf.specialtyIds || (selectedProf.specialtyId ? [selectedProf.specialtyId] : []))
    : [];
  const profSpecialties = allSpecialties.filter((s) => profSpecialtyIds.includes(s.id));

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Agenda de Profesionales</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configura los horarios de atención por clínica, profesional y especialidad.
      </Typography>
      {/* Existing Schedules List */}
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Agendas Existentes</Typography>
          {loadingSchedules && <CircularProgress size={20} />}
        </Box>
        {existingSchedules.length === 0 && !loadingSchedules ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No hay agendas configuradas</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Profesional</TableCell>
                  <TableCell>Sucursal</TableCell>
                  <TableCell>Clínica</TableCell>
                  <TableCell>Especialidad</TableCell>
                  <TableCell>Consulta</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingSchedules.map((sched) => (
                  <TableRow key={sched.id} hover sx={{ cursor: 'pointer' }} onClick={() => handlePreviewSchedule(sched)}>
                    <TableCell>{sched.professionalName}</TableCell>
                    <TableCell>{sched.branchName}</TableCell>
                    <TableCell>{sched.clinicName}</TableCell>
                    <TableCell>{sched.specialtyName}</TableCell>
                    <TableCell>{sched.capacityNumber}</TableCell>
                    <TableCell>
                      {sched.startDate && `${sched.startDate} ${sched.endDate ? `→ ${sched.endDate}` : '(indefinido)'}`}
                      {sched.recurrenceType === 'biweekly' && <Chip size="small" label="Quincenal" sx={{ ml: 1, fontSize: '0.65rem' }} />}
                      {sched.recurrenceType === 'monthly' && <Chip size="small" label={`Mensual (${sched.recurrenceWeekOrdinal || 'primera'})`} sx={{ ml: 1, fontSize: '0.65rem' }} />}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleEditSchedule(sched)}
                        sx={{ borderRadius: 2, mr: 1 }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteSchedule(sched)}
                        sx={{ borderRadius: 2 }}
                      >
                        Eliminar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {/* Selectores en cascada */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid
          size={{
            xs: 12,
            sm: 2
          }}>
          <Autocomplete
            value={branches.find((b) => b.id === selectedBranchId) || null}
            onChange={(e, value) => handleBranchChange(value?.id || '')}
            options={branches}
            getOptionLabel={(b) => b.name}
            renderInput={(params) => <TextField {...params} label="Sucursal" fullWidth InputProps={{ ...params.InputProps, sx: { fontSize: '0.95rem' } }} />}
            noOptionsText="No hay sucursales"
            clearText="Limpiar"
            ListboxProps={{ sx: { fontSize: '0.95rem' } }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 3
          }}>
          <Autocomplete
            value={clinics.find((c) => c.id === selectedClinicId) || null}
            onChange={(e, value) => handleClinicChange(value?.id || '')}
            options={selectedBranchId ? clinics.filter((c) => c.branchIds?.includes(selectedBranchId)) : clinics}
            getOptionLabel={(c) => c.name}
            disabled={!!selectedBranchId && clinics.filter((c) => c.branchIds?.includes(selectedBranchId)).length === 0}
            renderInput={(params) => <TextField {...params} label="Clínica" fullWidth InputProps={{ ...params.InputProps, sx: { fontSize: '0.95rem' } }} />}
            noOptionsText={selectedBranchId ? "No hay clínicas en esta sucursal" : "No hay clínicas"}
            clearText="Limpiar"
            ListboxProps={{ sx: { fontSize: '0.95rem' } }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 3
          }}>
          <Autocomplete
            value={filteredProfessionals.find((p) => p.id === selectedProfId) || null}
            onChange={(e, value) => handleProfChange(value?.id || '')}
            options={filteredProfessionals}
            getOptionLabel={(p) => `Dr/a. ${p.nombre} ${p.apellidos}`}
            disabled={!selectedClinicId}
            renderInput={(params) => <TextField {...params} label="Profesional" fullWidth InputProps={{ ...params.InputProps, sx: { fontSize: '0.95rem' } }} />}
            noOptionsText="No hay profesionales en esta clínica"
            clearText="Limpiar"
            ListboxProps={{ sx: { fontSize: '0.95rem' } }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 3
          }}>
          <Autocomplete
            value={profSpecialties.find((s) => s.id === selectedSpecialtyId) || null}
            onChange={(e, value) => handleSpecialtyChange(value?.id || '')}
            options={profSpecialties}
            getOptionLabel={(s) => s.name}
            disabled={!selectedProfId}
            renderInput={(params) => <TextField {...params} label="Especialidad" fullWidth InputProps={{ ...params.InputProps, sx: { fontSize: '0.95rem' } }} />}
            noOptionsText="Sin especialidades"
            clearText="Limpiar"
            ListboxProps={{ sx: { fontSize: '0.95rem' } }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 3
          }}>
          <Autocomplete
            value={filteredCapacities.find((c) => c.id === selectedCapacityId) || null}
            onChange={(e, value) => handleCapacityChange(value?.id || '')}
            options={filteredCapacities}
            getOptionLabel={(c) => c.number}
            disabled={!selectedSpecialtyId}
            renderInput={(params) => <TextField {...params} label="Consulta" fullWidth InputProps={{ ...params.InputProps, sx: { fontSize: '0.95rem' } }} />}
            noOptionsText={selectedBranchId ? "Sin consultas en esta sucursal" : "Sin consultas"}
            clearText="Limpiar"
            ListboxProps={{ sx: { fontSize: '0.95rem' } }}
          />
        </Grid>
      </Grid>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {!loading && selectedProfId && selectedClinicId && selectedSpecialtyId && selectedCapacityId && (
        <>
          {/* Card de información de capacidad */}
          {selectedCapacityData && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: 'primary.50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Información de Capacidad
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consulta {selectedCapacityData.number} · {selectedCapacityData.location}
                  </Typography>
                </Box>
                <Chip
                  label={`${selectedCapacityData.weeklyCapacity} hrs/semana`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Días de funcionamiento:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedCapacityData.operatingHours?.days?.map(day => (
                      <Chip key={day} label={DAY_NAMES[day]} size="small" variant="filled" color="info" />
                    ))}
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6
                  }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Horario:
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {selectedCapacityData.operatingHours?.startTime} - {selectedCapacityData.operatingHours?.endTime}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Sección: Fechas de vigencia */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Vigencia de la Agenda</Typography>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Fecha de inicio *"
                  type="date"
                  value={scheduleDates.startDate}
                  onChange={(e) => setScheduleDates({ ...scheduleDates, startDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: todayLocalStr() }}
                  helperText="La agenda será válida desde esta fecha"
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  label="Fecha de término (opcional)"
                  type="date"
                  value={scheduleDates.endDate}
                  onChange={(e) => setScheduleDates({ ...scheduleDates, endDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Déjalo vacío para término indefinido"
                />
              </Grid>
            </Grid>

            {/* Recurrencia */}
            <Divider sx={{ my: 2 }} />
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 700, mb: 1 }}>Recurrencia</FormLabel>
              <RadioGroup
                row
                value={recurrence.type}
                onChange={(e) => setRecurrence((prev) => ({ ...prev, type: e.target.value, weekOrdinal: e.target.value === 'monthly' ? prev.weekOrdinal || 'first' : '' }))}
              >
                <FormControlLabel value="continuous" control={<Radio size="small" />} label="Continua (semanal)" />
                <FormControlLabel value="biweekly" control={<Radio size="small" />} label="Semana por medio" />
                <FormControlLabel value="monthly" control={<Radio size="small" />} label="Una vez por mes" />
              </RadioGroup>
              {recurrence.type === 'biweekly' && (
                <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                  La agenda se repetirá cada dos semanas para los días definidos, comenzando desde la fecha de inicio.
                </Alert>
              )}
              {recurrence.type === 'monthly' && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    select
                    size="small"
                    label="Semana del mes"
                    value={recurrence.weekOrdinal || 'first'}
                    onChange={(e) => setRecurrence((prev) => ({ ...prev, weekOrdinal: e.target.value }))}
                    sx={{ minWidth: 180 }}
                  >
                    <MenuItem value="first">Primer</MenuItem>
                    <MenuItem value="second">Segundo</MenuItem>
                    <MenuItem value="third">Tercer</MenuItem>
                    <MenuItem value="last">Último</MenuItem>
                  </TextField>
                  <Typography variant="body2" color="text.secondary">
                    día del mes (según el día de la semana configurado abajo). Ej: "Primer lunes del mes".
                  </Typography>
                </Box>
              )}
            </FormControl>
          </Paper>

          {/* Indicador de disponibilidad de capacidad */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Disponibilidad de Capacidad</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <Typography variant="body2" color="text.secondary">Capacidad total:</Typography>
                <Typography variant="h6" fontWeight={600}>
                  {selectedCapacityData?.weeklyCapacity || 0} hrs/semana
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <Typography variant="body2" color="text.secondary">Horas de esta agenda:</Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {calculateScheduleHours(schedule).toFixed(1)} hrs/semana
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <Typography variant="body2" color="text.secondary">Disponible:</Typography>
                <Typography variant="h6" fontWeight={600} color={(selectedCapacityData?.weeklyCapacity || 0) - calculateScheduleHours(schedule) >= 0 ? 'success.main' : 'error.main'}>
                  {((selectedCapacityData?.weeklyCapacity || 0) - calculateScheduleHours(schedule)).toFixed(1)} hrs/semana
                </Typography>
              </Grid>
            </Grid>
            {(selectedCapacityData?.weeklyCapacity || 0) - calculateScheduleHours(schedule) < 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                La agenda excede la capacidad disponible. Ajusta los horarios o selecciona otra capacidad.
              </Alert>
            )}
          </Paper>

          {/* Sección: Horario semanal */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Horario Semanal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {clinics.find((c) => c.id === selectedClinicId)?.name} · Consulta {capacities.find((c) => c.id === selectedCapacityId)?.number} · {selectedProf?.nombre} {selectedProf?.apellidos} · {profSpecialties.find((s) => s.id === selectedSpecialtyId)?.name}
              </Typography>
            </Box>
            <Button
              variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveSchedule} disabled={saving} sx={{ borderRadius: 3 }}
            >
              Guardar Horario
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            {WORKING_DAYS.map((day) => {
              const cfg = schedule[String(day)] || { active: false, slots: [] };
              const isDayInCapacity = selectedCapacityData?.operatingHours?.days?.includes(day);
              const dayHasValidSlots = cfg.slots?.length > 0 && cfg.slots.every(slot => {
                const [slotHour, slotMin] = slot.time.split(':').map(Number);
                const slotTotal = slotHour * 60 + slotMin;
                const [capStartHour, capStartMin] = selectedCapacityData?.operatingHours?.startTime?.split(':').map(Number) || [0, 0];
                const [capEndHour, capEndMin] = selectedCapacityData?.operatingHours?.endTime?.split(':').map(Number) || [0, 0];
                const capStartTotal = capStartHour * 60 + capStartMin;
                const capEndTotal = capEndHour * 60 + capEndMin;
                return slotTotal >= capStartTotal && slotTotal < capEndTotal;
              });

              return (
                <Grid
                  key={day}
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Paper sx={{
                    p: 2, borderRadius: 3,
                    border: cfg.active ? '2px solid' : '2px solid transparent',
                    borderColor: cfg.active ? (isDayInCapacity ? 'success.light' : 'warning.light') : 'transparent',
                    bgcolor: cfg.active && !isDayInCapacity ? 'warning.50' : 'background.paper',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{DAY_NAMES[day]}</Typography>
                        {cfg.active && (
                          <Chip
                            label={isDayInCapacity ? 'Dentro de horario' : 'Fuera de horario'}
                            size="small"
                            color={isDayInCapacity ? 'success' : 'warning'}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Copiar horarios a todos los días">
                          <IconButton size="small" onClick={() => copyToAllDays(String(day))}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <FormControlLabel
                          control={<Switch checked={!!cfg.active} onChange={() => toggleDay(String(day))} color="success" size="small" />}
                          label={cfg.active ? 'Habilitado' : 'Deshabilitado'}
                          sx={{ mr: 0 }}
                        />
                      </Box>
                    </Box>

                    {!isDayInCapacity && cfg.active && (
                      <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                        Este día no está en el horario de funcionamiento de la capacidad
                      </Alert>
                    )}

                    {cfg.slots && cfg.slots.length > 0 ? (
                      <TableContainer sx={{ maxHeight: 200, mb: 1.5 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Hora</strong></TableCell>
                              <TableCell align="center"><strong>Máx. pacientes</strong></TableCell>
                              <TableCell align="center"><strong>Online</strong></TableCell>
                              <TableCell align="center"></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cfg.slots.map((slot) => {
                              const [slotHour, slotMin] = slot.time.split(':').map(Number);
                              const slotTotal = slotHour * 60 + slotMin;
                              const [capStartHour, capStartMin] = selectedCapacityData?.operatingHours?.startTime?.split(':').map(Number) || [0, 0];
                              const [capEndHour, capEndMin] = selectedCapacityData?.operatingHours?.endTime?.split(':').map(Number) || [0, 0];
                              const capStartTotal = capStartHour * 60 + capStartMin;
                              const capEndTotal = capEndHour * 60 + capEndMin;
                              const isSlotValid = slotTotal >= capStartTotal && slotTotal < capEndTotal;
                              const isSlotOccupied = occupiedSlots[String(day)]?.includes(slot.time);

                              return (
                                <TableRow key={slot.time} sx={{
                                  bgcolor: !isSlotValid ? 'warning.50' : (isSlotOccupied ? 'error.50' : 'inherit'),
                                  opacity: isSlotOccupied ? 0.6 : 1
                                }}>
                                  <TableCell>
                                    {slot.label || slot.time}
                                    {!isSlotValid && (
                                      <Tooltip title="Fuera del horario de funcionamiento">
                                        <span style={{ color: 'orange', marginLeft: 4 }}>⚠</span>
                                      </Tooltip>
                                    )}
                                    {isSlotOccupied && (
                                      <Tooltip title="Horario ocupado por otra agenda">
                                        <span style={{ color: 'red', marginLeft: 4 }}>🔒</span>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <TextField
                                      type="number" value={slot.capacity}
                                      onChange={(e) => updateCapacity(String(day), slot.time, e.target.value)}
                                      size="small" inputProps={{ min: 1, max: 20, style: { textAlign: 'center', width: 45 } }}
                                      variant="standard"
                                      disabled={isSlotOccupied}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Checkbox
                                      checked={slot.visibleOnline !== false}
                                      onChange={() => toggleVisibleOnline(String(day), slot.time)}
                                      size="small"
                                      disabled={isSlotOccupied}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <IconButton size="small" color="error" onClick={() => removeSlot(String(day), slot.time)} disabled={isSlotOccupied}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                        Sin horarios
                      </Typography>
                    )}

                    <Divider sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        label="Hora" type="time" value={newSlot.time}
                        onChange={(e) => setNewSlot({ ...newSlot, time: e.target.value })}
                        size="small" InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Máx." type="number" value={newSlot.capacity}
                        onChange={(e) => setNewSlot({ ...newSlot, capacity: e.target.value })}
                        size="small" inputProps={{ min: 1, max: 20 }} sx={{ width: 70 }}
                      />
                      <IconButton
                        color="primary"
                        onClick={() => addSlot(String(day))}
                        disabled={!newSlot.time || occupiedSlots[String(day)]?.includes(newSlot.time)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Sección: Bloqueos */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Bloqueos / Ausencias</Typography>
              <Typography variant="body2" color="text.secondary">
                Registra días u horarios en que el profesional no estará disponible
              </Typography>
            </Box>
            <Button variant="outlined" color="warning" startIcon={<BlockIcon />} onClick={() => setBlockDialog(true)} sx={{ borderRadius: 3 }}>
              Agregar Bloqueo
            </Button>
          </Box>

          {blocks.length === 0 ? (
            <Alert severity="success" icon={<CalendarMonthIcon />}>
              No hay bloqueos registrados. La agenda está disponible según el horario configurado.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Tipo</strong></TableCell>
                    <TableCell><strong>Horario</strong></TableCell>
                    <TableCell><strong>Motivo</strong></TableCell>
                    <TableCell align="center"><strong>Eliminar</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blocks.map((block) => (
                    <TableRow key={block.id} hover>
                      <TableCell>{block.date}</TableCell>
                      <TableCell>
                        <Chip
                          label={block.blockType === 'full_day' ? 'Día completo' : 'Rango horario'}
                          size="small"
                          color={block.blockType === 'full_day' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        {block.blockType === 'time_range'
                          ? `${block.startTime} – ${block.endTime}`
                          : '—'}
                      </TableCell>
                      <TableCell>{block.reason}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleDeleteBlock(block.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      {(!selectedProfId || !selectedClinicId || !selectedSpecialtyId || !selectedCapacityId) && !loading && (
        <Alert severity="info">Selecciona Sucursal (opcional), Clínica, Profesional, Especialidad y Capacidad para configurar la agenda.</Alert>
      )}
      {/* Dialog: Agregar Bloqueo */}
      <Dialog open={blockDialog} onClose={() => { setBlockDialog(false); setBlockForm(EMPTY_BLOCK_FORM); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Agregar Bloqueo / Ausencia</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <TextField
                label="Fecha" type="date" value={blockForm.date}
                onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }} fullWidth required
                inputProps={{ min: todayLocalStr() }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Motivo" value={blockForm.reason}
                onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                fullWidth required placeholder="Ej: Vacaciones, Congresos, Enfermedad..."
              />
            </Grid>
            <Grid size={12}>
              <TextField
                select label="Tipo de bloqueo" value={blockForm.blockType}
                onChange={(e) => setBlockForm({ ...blockForm, blockType: e.target.value })}
                fullWidth
              >
                <MenuItem value="full_day">Día completo</MenuItem>
                <MenuItem value="time_range">Rango de horario</MenuItem>
              </TextField>
            </Grid>
            {blockForm.blockType === 'time_range' && (
              <>
                <Grid size={6}>
                  <TextField
                    label="Hora inicio" type="time" value={blockForm.startTime}
                    onChange={(e) => setBlockForm({ ...blockForm, startTime: e.target.value })}
                    InputLabelProps={{ shrink: true }} fullWidth required
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    label="Hora fin" type="time" value={blockForm.endTime}
                    onChange={(e) => setBlockForm({ ...blockForm, endTime: e.target.value })}
                    InputLabelProps={{ shrink: true }} fullWidth required
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setBlockDialog(false); setBlockForm(EMPTY_BLOCK_FORM); }} variant="outlined">Cancelar</Button>
          <Button onClick={handleAddBlock} variant="contained" color="warning" disabled={savingBlock}>
            {savingBlock ? <CircularProgress size={18} /> : 'Registrar Bloqueo'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog: Vista Previa de Agenda */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, schedule: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Vista Previa de Agenda
        </DialogTitle>
        <DialogContent>
          {previewDialog.schedule && (
            <Box sx={{ pt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                <Grid container spacing={2}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">Profesional:</Typography>
                    <Typography variant="body1" fontWeight={600}>{previewDialog.schedule.professionalName}</Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">Sucursal:</Typography>
                    <Typography variant="body1" fontWeight={600}>{previewDialog.schedule.branchName}</Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">Clínica:</Typography>
                    <Typography variant="body1" fontWeight={600}>{previewDialog.schedule.clinicName}</Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">Especialidad:</Typography>
                    <Typography variant="body1" fontWeight={600}>{previewDialog.schedule.specialtyName}</Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Typography variant="body2" color="text.secondary">Capacidad:</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Consulta {previewDialog.schedule.capacityNumber} · {previewDialog.schedule.capacityLocation}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="body2" color="text.secondary">Vigencia:</Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {previewDialog.schedule.startDate} {previewDialog.schedule.endDate ? `→ ${previewDialog.schedule.endDate}` : '(indefinido)'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Horario Semanal</Typography>
              <Grid container spacing={2}>
                {WORKING_DAYS.map((day) => {
                  const dayConfig = previewDialog.schedule.schedule?.[String(day)];
                  if (!dayConfig || !dayConfig.active) return null;

                  return (
                    <Grid
                      key={day}
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {DAY_NAMES[day]}
                        </Typography>
                        {dayConfig.slots && dayConfig.slots.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {dayConfig.slots.map((slot) => (
                              <Chip
                                key={slot.time}
                                label={`${slot.time} (${slot.capacity})`}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Sin horarios</Typography>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {!Object.values(previewDialog.schedule.schedule || {}).some(d => d?.active) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Esta agenda no tiene días activos configurados.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, schedule: null })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfessionalScheduleManager;
